import { MeetingContainerAPI, MeetingSourceElement, MeetingSourceElementRef } from "@kalyzee/kast-web-sdk";
import { MeetingSourceExtended, useMeetingSelector } from "../../hooks/meeting";
import React, { createRef, useEffect, useMemo, useRef } from "react";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { MediaSourcesCarousel, MediaCarouselSource } from "@kalyzee/kalyzee-ui";
import { toastError } from "../../helpers/toast";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../contexts/appStore/context";

export interface MediaSourcePickerProps {
  meetingAPI: MeetingContainerAPI;
  display?: boolean;
  sources: MeetingSourceExtended[];
  style?: React.CSSProperties;
  className?: string;
}

(window as any).tracks = {};

const _MediaSourcePicker: React.FC<MediaSourcePickerProps> = ({
  meetingAPI,
  display,
  sources,
  style,
  className,
}) => {
  const meetingAPIRef = useRefAutoUpdate(meetingAPI);
  const sourcesRef = useRef<{ [key: string]: React.RefObject<MeetingSourceElementRef> }>({});

  const [medias] = useMeetingSelector(meetingAPI, (state) => state.session?.medias);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const dispatch = useAppStoreDispatch();

  useEffect(() => {
    const refs = Object.values(sourcesRef.current);
    refs.forEach((r) => {
      const sourceElementRef = r.current;
      if (!sourceElementRef) return;
      const source = sources.find((s) => s === sourceElementRef.getData()?.source);
      if (!source) return;
      sourceElementRef.enableAudio(!!audioEnabled);
      sourceElementRef.enableVideo(!!videoEnabled);
    });
  }, [audioEnabled, videoEnabled, sources]);

  const renderSource = (source: MeetingSourceExtended) => {
    if (!sourcesRef.current[source.name!]) sourcesRef.current[source.name!] = createRef();
    const srcType = source.extra?.type;
    return (
      <MeetingSourceElement
        className="meeting-source-expert"
        style={{ height: "100%", width: "100%", border: "unset", backgroundColor: "unset" }}
        ref={sourcesRef.current[source.name!]}
        key={source.name}
        source={source}
        using
        audioContext={getAudioContextForVolumeAnalyzer()}
        callbacks={{
          track: async (track, data) => {
            const api = meetingAPIRef.current;
            if (!api) return;
            const mediaSession = medias?.find((m) => m.name === source.name);
            if (!mediaSession) return;
            const media = data.media;
            await api?.setMediaStream(mediaSession.id, media);
            dispatch("updateSelfStream", { mediaId: mediaSession.id, stream: media });
          },
          error: (type) => {
            if (type === "audio") {
              toastError("Veuillez autoriser le navigateur à accéder au microphone");
              meetingAPIRef.current?.mute();
            }
            if (type === "video") {
              toastError("Veuillez autoriser le navigateur à accéder à la caméra");
              if (srcType === "screen") meetingAPIRef.current?.disableVideo({ direction: "outgoing", group: "screens" });
              if (srcType === "camera") meetingAPIRef.current?.disableVideo({ direction: "outgoing", group: "cameras" });
            }
          },
        }}
      />
    );
  };

  const sourceItems = useMemo(() => {
    return sources.map<MediaCarouselSource>((source) => ({
      id: `media-source-${source.id}`,
      name: source.name,
      content: renderSource(source),
      visible: source.visible,
    }));
  }, [sources]);

  return (
    <MediaSourcesCarousel
      sources={sourceItems}
      display={display}
      style={style}
      className={className}
    />
  );
}

export const MediaSourcePicker = React.memo(_MediaSourcePicker);